/* eslint-disable clubmed/no-fake-component */
import React, { Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Collapse } from 'react-collapse';
import { Button } from 'Components/frameworks/button/Button';
import type { ButtonProps } from 'Components/frameworks/button/Button';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { Text } from 'Components/frameworks/text/Text';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { truncate } from 'Components/utils/string/string.util';
import { t } from 'Src/i18n';
import { capitalize } from 'Utils/string/stringUtils';
import { Image } from 'Components/frameworks/image/Image';
import { ImageSlideShow } from 'Components/frameworks/image-slideshow/ImageSlideShow';
import ratiosUtils from 'Components/utils/ratios/ratios.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';

const getListValue = (value) =>
  value?.filter(Boolean).length ? (
    <ul className="font-bold">
      {value.map((v) => (
        <li className="flex items-center justify-between" key={v.id}>
          {v.label}
          <span
            className={classnames('font-brand font-normal', {
              'text-green': v.isIncluded,
              'text-orange': !v.isIncluded,
            })}
          >
            {t(v.isIncluded ? 'common.included' : 'common.onDemand')}
          </span>
        </li>
      ))}
    </ul>
  ) : null;

const getTextValue = (value: string) =>
  value ? <Text className="font-bold" text={value} /> : null;

type RemainingStock = {
  quantity?: number;
};
type Props = {
  className?: string;
  description?: string | null | undefined;
  duration?: string | null | undefined;
  hasReverseLayout?: boolean;
  id: string;
  images?: Array<string>;
  isAdded?: boolean;
  isIncluded?: boolean;
  isLoading?: boolean;
  isUnavailable?: boolean;
  isCartLocked?: boolean;
  list?: Array<{
    label: string;
    note?: string | null | undefined;
    value:
      | (string | null | undefined)
      | Array<{
          id: string;
          isIncluded: boolean;
          label: string;
        }>;
  }>;
  name: string;
  note?: string | null | undefined;
  onBook?: (() => void) | null | undefined;
  isExpanded?: boolean;
  price: ExtendedPrice | null | undefined;
  remainingStock?: RemainingStock | null | undefined;
  subtitle?: string | null | undefined;
  showSliderOfImages?: boolean;
};
export const FeatureCard = ({
  className,
  description,
  duration,
  hasReverseLayout = false,
  id,
  images = [],
  isAdded = false,
  isIncluded = false,
  isLoading = false,
  isUnavailable = false,
  isCartLocked = false,
  list = [],
  name,
  note,
  onBook,
  price,
  remainingStock,
  subtitle,
  isExpanded,
  showSliderOfImages = false,
}: Props) => {
  const { isRTL } = useResponsive();
  const discount = Math.abs(Math.round(Number(price?.discount)));
  const hasDiscount = discount >= 5;
  return (
    <article
      className={classnames(
        'relative isolate flex flex-col rounded-12 bg-white shadow',
        {
          'opacity-50': isUnavailable && !!images.length,
        },
        className,
      )}
      id={id}
    >
      {images.length > 1 && showSliderOfImages && (
        <ImageSlideShow
          hasLazyLoading
          itemClassName="rounded-t-12 isolate overflow-hidden will-change-transform"
          media={images.map((src) => ({
            src,
          }))}
          ratio={ratiosUtils.XL}
          ratioDisplay={1 / 3}
          hasOverlappingClick
          hasInfiniteScroll
        />
      )}
      {(images.length > 0 && !showSliderOfImages) ||
        (images.length === 1 && (
          <Image
            className="overflow-hidden rounded-t-12"
            imageUrl={images[0]}
            ratioKey={ratiosUtils.XL}
          />
        ))}

      <div className="flex min-h-200 flex-col p-15 md:p-20">
        <header className="mb-auto flex min-h-42 items-baseline justify-between">
          <div className="w-full max-w-220 flex-1 pe-15">
            <h3 className="font-brand text-16 font-bold leading-normal text-black">{name}</h3>
            {!isAdded && remainingStock && (
              <p className="mt-5 flex items-center gap-5 text-14 font-bold leading-1 text-red">
                <Icon className="shrink-0" name={ICONS.WARNING} width="18px" />
                {t('booking.activities.remainingService', {
                  nb: Number(remainingStock.quantity),
                })}
              </p>
            )}
            {subtitle && <h4 className="text-14">{subtitle}</h4>}
            {isUnavailable && (
              <p className="mt-5 flex items-center gap-5 text-14 font-bold leading-1 text-red">
                <Icon className="shrink-0" name={ICONS.WARNING} width="18px" />
                {t('booking.activities.unavailableActivity')}
              </p>
            )}
          </div>
          {isIncluded && <p className="font-bold text-green">{t('common.included')}</p>}
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            <Fragment>
              {!isIncluded && !isAdded && (
                <div>
                  {price?.value ? (
                    <Fragment>
                      {hasDiscount && (
                        <p className="-translate-y-50 absolute end-0 top-0 mx-15 -mt-5 rounded-pill bg-sienna px-10 py-5 text-12 font-bold leading-1 text-white">
                          {t('common.price.variationUpTo', {
                            discount: `${discount}%`,
                          })}
                        </p>
                      )}
                      <p className="flex flex-col text-end text-12">
                        <span className="font-sans text-12 font-normal text-black">
                          {capitalize(t('price.format.from'))}
                        </span>{' '}
                        <span className={classnames('font-brand text-16 text-black')}>
                          {price.formattedInitialValue && hasDiscount && (
                            <s className="me-5 text-12 text-sienna line-through">
                              {price.formattedInitialValue}
                            </s>
                          )}
                          {price.formattedValue}
                        </span>
                        <span>{t('price.format.unitPerPerson')}</span>
                      </p>
                    </Fragment>
                  ) : (
                    <p className="max-w-80 text-end text-12 leading-tight text-mediumGrey">
                      {t('common.price.checkInResort')}
                    </p>
                  )}
                </div>
              )}
              {!isIncluded && isAdded && (
                <div className="text-center text-12">
                  <Icon className="text-success" name={ICONS.CHECK} width="1em" />
                  <span className="block">{t('common.added')}</span>
                </div>
              )}
            </Fragment>
          )}
        </header>
        <div
          dir={isRTL ? 'rtl' : 'ltr'}
          className={classnames('mt-40 flex', {
            'flex-col': isExpanded,
          })}
        >
          <div>
            {duration && (
              <p className="mb-5 flex items-center gap-10 text-14 leading-0">
                <Icon className="text-sienna" name={ICONS.DURATION} width="1.375rem" />
                <span className="sr-only">{t('common.durationLabel')}</span>
                {duration}
              </p>
            )}
            <Collapse
              initialStyle={{
                minHeight: 45,
              }}
              isOpened={isExpanded}
            >
              <div className="flex flex-col">
                <div className="text-initial py-5">
                  {!!description && (
                    <Text
                      className={classnames({
                        'line-clamp-2': !isExpanded,
                      })}
                      text={isExpanded ? description : truncate(description, 100, true)}
                    />
                  )}
                </div>
                {!!list.length && (
                  <ul
                    className={classnames('py-5 text-14', {
                      'order-first': hasReverseLayout,
                    })}
                  >
                    {list.map((l) =>
                      l.value?.length ? (
                        <li className="mb-15" key={l.label}>
                          {l.label && <span className="me-10 shrink-0">{l.label}</span>}
                          <div>
                            {Array.isArray(l.value) ? getListValue(l.value) : getTextValue(l.value)}
                            {l.note && <Text className="mt-5" text={l.note} theme="text-sans-n2" />}
                          </div>
                        </li>
                      ) : null,
                    )}
                  </ul>
                )}
                {!isIncluded && !isCartLocked && onBook && price?.value && (
                  <Button
                    {...({
                      className: classnames(
                        'mb-15 mt-25 w-full transition-all duration-250 ease-in',
                        {
                          'opacity-0': !isExpanded,
                        },
                      ),
                      color: COLORS.BLACK,
                      colorBg: isAdded ? COLORS.TRANSPARENT : COLORS.SAFFRON,
                      colorBorder: isAdded ? COLORS.BLACK : COLORS.SAFFRON,
                      'data-testid': 'Add-activity',
                      isLoading,
                      onClick: (e) => {
                        e.stopPropagation();
                        onBook && onBook();
                      },
                      tabIndex: isExpanded ? 0 : -1,
                    } as ButtonProps & { 'data-testid'?: string; tabIndex?: number })}
                  >
                    {t(isAdded ? 'booking.common.editPrice' : 'booking.common.addPrice', {
                      price: `+${price?.formattedValue}`,
                    })}
                  </Button>
                )}
                {note && <Text className="-mb-10 pe-20" text={note.trim()} theme="text-sans-n2" />}
              </div>
            </Collapse>
          </div>
          <div
            className={classnames('flex justify-end self-end ps-20', {
              'pb-5': isExpanded,
            })}
            data-testid="See-more-details"
          >
            <span className="sr-only">{t(isExpanded ? 'common.close' : 'common.viewMore')}</span>
            <Icon name={ICONS.ARROW_DOWN} rotation={isExpanded ? 180 : 0} width="0.9375rem" />
          </div>
        </div>
      </div>
    </article>
  );
};
